import React, { useMemo, useState } from 'react';
import { MAINTENANCE_CATEGORY_ICONS } from './config/trackers';

/**
 * Maintenance Orders — custom Orders view (spec § 17).
 *
 * Replaces both the Accounts and Payments tabs. Shows status sub-tabs
 * (Open / In Progress / Completed) with counts, and groups orders by
 * fields.orderLocation. Clicking an order opens an inline order-detail
 * popup with quick actions (add quote, add note, add photo) that render
 * inline forms (no window.prompt or window.confirm calls).
 */

const STATUS_TABS = [
    { id: 'open', icon: '📋', label: 'Open' },
    { id: 'inprogress', icon: '🔧', label: 'In Progress' },
    { id: 'completed', icon: '✓', label: 'Completed' },
];

function statusBucket(status) {
    if (status === 'Requested' || status === 'Assigned') return 'open';
    if (status === 'In Progress') return 'inprogress';
    if (status === 'Completed' || status === 'Cancelled') return 'completed';
    return 'open';
}

function priorityKey(priority) {
    if (!priority) return 'normal';
    const p = priority.toLowerCase();
    if (p.startsWith('urgent')) return 'urgent';
    if (p.startsWith('low')) return 'low';
    return 'normal';
}

function moneyToNumber(s) {
    const n = Number(String(s || '').replace(/[^\d.]/g, ''));
    return Number.isFinite(n) ? n : 0;
}

function nowStamp() {
    const d = new Date();
    return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
}

function todayIso() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatPretty(iso) {
    if (!iso) return '—';
    try {
        const d = new Date(`${iso}T00:00`);
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) {
        return iso;
    }
}

function lowestQuoteAmount(quotes) {
    const arr = (quotes || [])
        .map((q) => moneyToNumber(q.amount))
        .filter((n) => n > 0);
    if (arr.length === 0) return 0;
    return Math.min(...arr);
}

function buildPayload(order, patch) {
    return {
        accountId: order.accountId,
        trackerKey: 'maintenance',
        label: patch.label != null ? patch.label : order.label,
        belongs: patch.belongs != null ? patch.belongs : order.belongs,
        frequency: 'asNeeded',
        fields: { ...(order.fields || {}), ...(patch.fields || {}) },
        paymentMethod: order.paymentMethod,
        months: order.months,
        subTaxes: order.subTaxes,
    };
}

function MaintenanceView({ accounts, api, showToast }) {
    const [tab, setTab] = useState('open');
    const [openLoc, setOpenLoc] = useState(null);
    const [detailTarget, setDetailTarget] = useState(null);

    const orders = useMemo(() => accounts || [], [accounts]);

    const counts = useMemo(() => {
        const c = { open: 0, inprogress: 0, completed: 0 };
        orders.forEach((o) => {
            const b = statusBucket(o.fields?.orderStatus || 'Requested');
            c[b] += 1;
        });
        return c;
    }, [orders]);

    const filtered = useMemo(
        () => orders.filter(
            (o) => statusBucket(o.fields?.orderStatus || 'Requested') === tab,
        ),
        [orders, tab],
    );

    const groups = useMemo(() => {
        const map = new Map();
        filtered.forEach((o) => {
            const key = o.fields?.orderLocation || '(no location)';
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(o);
        });
        const out = [];
        Array.from(map.entries()).forEach(([loc, list]) => {
            const sorted = [...list].sort((a, b) => {
                const pa = priorityKey(a.fields?.priority);
                const pb = priorityKey(b.fields?.priority);
                const score = (k) => (k === 'urgent' ? 0 : k === 'normal' ? 1 : 2);
                const diff = score(pa) - score(pb);
                if (diff !== 0) return diff;
                return (b.fields?.dateRequested || '').localeCompare(
                    a.fields?.dateRequested || '',
                );
            });
            out.push({ loc, orders: sorted });
        });
        out.sort((a, b) => a.loc.localeCompare(b.loc));
        return out;
    }, [filtered]);

    const refreshDetail = (accountId) => {
        const next = orders.find((o) => o.accountId === accountId);
        if (next) setDetailTarget(next);
    };

    return (
        <>
            {/* Status sub-tabs */}
            <div className="bt-mtnce-tabs">
                {STATUS_TABS.map((t) => (
                    <button
                        key={t.id}
                        type="button"
                        className={`bt-mtnce-tab ${tab === t.id ? 'active' : ''}`}
                        onClick={() => setTab(t.id)}
                    >
                        <span role="img" aria-label={t.label}>{t.icon}</span> {t.label}
                        <span className="bt-mtnce-tab-count">{counts[t.id]}</span>
                    </button>
                ))}
            </div>

            {groups.length === 0 ? (
                <div className="bt-empty">
                    No {tab === 'open' ? 'open' : tab === 'inprogress' ? 'in-progress' : 'completed'} orders.
                </div>
            ) : (
                <div className="bt-loc-list">
                    {groups.map((g) => (
                        <OrderLocationCard
                            key={g.loc}
                            group={g}
                            open={openLoc === g.loc}
                            onToggle={() => setOpenLoc(openLoc === g.loc ? null : g.loc)}
                            onOrderClick={(o) => setDetailTarget(o)}
                        />
                    ))}
                </div>
            )}

            {detailTarget && (
                <OrderDetailPopup
                    key={detailTarget.accountId}
                    order={
                        orders.find((o) => o.accountId === detailTarget.accountId)
                            || detailTarget
                    }
                    onClose={() => setDetailTarget(null)}
                    api={api}
                    showToast={showToast}
                    onRefresh={refreshDetail}
                />
            )}
        </>
    );
}

function OrderLocationCard({ group, open, onToggle, onOrderClick }) {
    const urgentCount = group.orders.filter(
        (o) => priorityKey(o.fields?.priority) === 'urgent',
    ).length;

    return (
        <div className={`bt-loc-card${open ? ' expanded' : ''}`}>
            <div
                className="bt-loc-head"
                onClick={onToggle}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onToggle()}
            >
                <div className="bt-loc-info">
                    <div className="bt-loc-name">{group.loc}</div>
                    <div className="bt-loc-meta">
                        {urgentCount > 0 && (
                            <span className="bt-tag alert">
                                <span role="img" aria-label="Urgent">🔴</span> {urgentCount} urgent
                            </span>
                        )}
                        <span className="bt-tag">
                            {group.orders.length} order{group.orders.length === 1 ? '' : 's'}
                        </span>
                    </div>
                </div>
                <div className="bt-loc-chevron" aria-hidden="true">▼</div>
            </div>

            {open && (
                <div className="bt-loc-body">
                    <div className="bt-order-list">
                        {group.orders.map((o, i) => (
                            <OrderRow
                                key={o.accountId}
                                order={o}
                                index={i}
                                onClick={() => onOrderClick(o)}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function OrderRow({ order, index, onClick }) {
    const f = order.fields || {};
    const status = f.orderStatus || 'Requested';
    const pKey = priorityKey(f.priority);
    const category = f.category || 'General';
    const catIcon = MAINTENANCE_CATEGORY_ICONS[category] || '🔧';
    const quotes = Array.isArray(f.quotes) ? f.quotes : [];
    const photos = Array.isArray(f.photos) ? f.photos : [];

    const statusKey = status.toLowerCase().replace(/ /g, '');

    let costLabel = '—';
    if (status === 'Completed' && f.actualCost) {
        costLabel = `${f.actualCost} actual`;
    } else if (quotes.length > 0) {
        const low = lowestQuoteAmount(quotes);
        if (low > 0) costLabel = `$${low.toLocaleString()} quoted`;
        else if (f.quotedCost) costLabel = `${f.quotedCost} quoted`;
    } else if (f.quotedCost) {
        costLabel = `${f.quotedCost} quoted`;
    }

    let quotePill = null;
    if (status === 'Requested' && quotes.length === 0) {
        quotePill = (
            <span className="bt-order-pill noquote">
                <span role="img" aria-label="Money">💰</span> No quotes yet
            </span>
        );
    } else if (status === 'Requested' && quotes.length > 1) {
        quotePill = (
            <span className="bt-order-pill choose">
                <span role="img" aria-label="Money">💰</span> {quotes.length} quotes · choose
            </span>
        );
    } else if (quotes.length > 0) {
        quotePill = (
            <span className="bt-order-pill quotes">
                <span role="img" aria-label="Money">💰</span> {quotes.length} quote{quotes.length === 1 ? '' : 's'}
            </span>
        );
    }

    return (
        <div
            className={`bt-order-row status-${statusKey}`}
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && onClick()}
        >
            <div className="bt-order-num">#{index + 1}</div>
            <div className="bt-order-icon" role="img" aria-label={category}>{catIcon}</div>
            <div className="bt-order-info">
                <div className="bt-order-title">{order.label || '(untitled)'}</div>
                <div className="bt-order-pills">
                    <span className={`bt-order-pill priority-${pKey}`}>
                        {pKey === 'urgent' ? 'Urgent' : pKey === 'low' ? 'Low' : 'Normal'}
                    </span>
                    <span className="bt-order-pill cat">{category}</span>
                    {quotePill}
                    {photos.length > 0 && (
                        <span className="bt-order-pill photos">
                            <span role="img" aria-label="Photos">📷</span> {photos.length}
                        </span>
                    )}
                </div>
                <div className="bt-order-sub">
                    <span><span role="img" aria-label="Vendor">👷</span> {f.vendor || 'TBD'}</span>
                    <span>·</span>
                    <span><span role="img" aria-label="Date">📅</span> {formatPretty(f.dateRequested)}</span>
                </div>
            </div>
            <div className="bt-order-right">
                <div className={`bt-order-status-badge status-${statusKey}`}>{status}</div>
                <div className="bt-order-cost">{costLabel}</div>
            </div>
        </div>
    );
}

function OrderDetailPopup({ order, onClose, api, showToast, onRefresh }) {
    const [activeAction, setActiveAction] = useState(null);
    const [saving, setSaving] = useState(false);

    const f = order.fields || {};
    const status = f.orderStatus || 'Requested';
    const statusKey = status.toLowerCase().replace(/ /g, '');
    const quotes = Array.isArray(f.quotes) ? f.quotes : [];
    const photos = Array.isArray(f.photos) ? f.photos : [];
    const updates = Array.isArray(f.updates) ? f.updates : [];

    const isClosed = status === 'Completed' || status === 'Cancelled';
    const category = f.category || 'General';

    const persist = (patch, msg) => {
        if (saving) return;
        setSaving(true);
        const payload = buildPayload(order, patch);
        api.saveAccount(payload, (err) => {
            setSaving(false);
            if (!err) {
                if (msg) showToast(msg, 'success');
                onRefresh(order.accountId);
            }
        });
    };

    const appendUpdate = (entry) => ({
        ...entry,
        date: nowStamp(),
    });

    const handleAddQuote = (vendor, amount, notes) => {
        const newQuote = {
            vendor: (vendor || '').trim(),
            amount: (amount || '').trim(),
            notes: (notes || '').trim(),
            date: nowStamp(),
            assigned: false,
        };
        const newQuotes = [...quotes, newQuote];
        const newUpdates = [
            ...updates,
            appendUpdate({
                icon: '💰',
                type: 'Quote received',
                text: `<strong>${newQuote.vendor || 'Vendor'}</strong> quoted ${newQuote.amount || '—'}`,
            }),
        ];
        const lowest = lowestQuoteAmount(newQuotes);
        const fields = {
            ...f,
            quotes: newQuotes,
            updates: newUpdates,
        };
        if (status === 'Requested' && lowest > 0) {
            fields.quotedCost = `$${lowest.toLocaleString()}`;
        }
        persist({ fields }, 'Quote added');
        setActiveAction(null);
    };

    const handleAddNote = (text) => {
        const trimmed = (text || '').trim();
        if (!trimmed) return;
        const newUpdates = [
            ...updates,
            appendUpdate({ icon: '📝', type: 'Note added', text: trimmed }),
        ];
        persist({ fields: { ...f, updates: newUpdates } }, 'Note added');
        setActiveAction(null);
    };

    const handleAddPhoto = (description) => {
        const desc = (description || '').trim();
        if (!desc) return;
        const newPhotos = [...photos, { name: desc, date: nowStamp() }];
        const newUpdates = [
            ...updates,
            appendUpdate({ icon: '📷', type: 'Photo added', text: desc }),
        ];
        persist(
            { fields: { ...f, photos: newPhotos, updates: newUpdates } },
            'Photo entry added',
        );
        setActiveAction(null);
    };

    const handleChooseVendor = (idx) => {
        const chosen = quotes[idx];
        if (!chosen) return;
        const newQuotes = quotes.map((q, i) => ({ ...q, assigned: i === idx }));
        const newUpdates = [
            ...updates,
            appendUpdate({
                icon: '✓',
                type: 'Vendor assigned',
                text: `<strong>${chosen.vendor || 'Vendor'}</strong> chosen at ${chosen.amount || '—'}`,
            }),
        ];
        persist(
            {
                fields: {
                    ...f,
                    quotes: newQuotes,
                    vendor: chosen.vendor,
                    quotedCost: chosen.amount,
                    orderStatus: 'Assigned',
                    updates: newUpdates,
                },
            },
            'Vendor assigned',
        );
    };

    const handleRemoveQuote = (idx) => {
        const newQuotes = quotes.filter((_, i) => i !== idx);
        persist({ fields: { ...f, quotes: newQuotes } }, 'Quote removed');
    };

    const handleRemovePhoto = (idx) => {
        const newPhotos = photos.filter((_, i) => i !== idx);
        persist({ fields: { ...f, photos: newPhotos } }, 'Photo removed');
    };

    const handleStartWork = () => {
        const newUpdates = [
            ...updates,
            appendUpdate({
                icon: '🔧',
                type: 'Work started',
                text: 'Order moved to In Progress',
            }),
        ];
        persist(
            { fields: { ...f, orderStatus: 'In Progress', updates: newUpdates } },
            'Work started',
        );
    };

    const handleMarkCompleted = (cost) => {
        const final = (cost || '').trim();
        const newUpdates = [
            ...updates,
            appendUpdate({
                icon: '✓',
                type: 'Order completed',
                text: `Final cost: <strong>${final || '—'}</strong>`,
            }),
        ];
        persist(
            {
                fields: {
                    ...f,
                    orderStatus: 'Completed',
                    actualCost: final,
                    dateCompleted: todayIso(),
                    updates: newUpdates,
                },
            },
            'Order completed',
        );
        setActiveAction(null);
    };

    const handleAssignScroll = () => {
        setActiveAction(null);
        const el = document.getElementById('bt-quotes-section');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const pickedQuote = quotes.find((q) => q.assigned);

    return (
        <div className="bt-overlay" onClick={onClose}>
            <div
                className="bt-modal bt-order-modal"
                role="dialog"
                aria-modal="true"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="bt-modal-head">
                    <h2>
                        <span role="img" aria-label={category}>
                            {MAINTENANCE_CATEGORY_ICONS[category] || '🔧'}
                        </span>{' '}
                        {order.label || '(untitled order)'}
                    </h2>
                    <button
                        className="bt-modal-close"
                        onClick={onClose}
                        type="button"
                        aria-label="Close"
                    >
                        ✕
                    </button>
                </div>
                <div className="bt-order-detail-sub">
                    {f.orderLocation || '—'}
                    {category ? ` — ${category}` : ''}
                </div>
                <div className={`bt-order-detail-badge status-${statusKey}`}>{status}</div>

                {f.description && (
                    <div className="bt-order-desc">{f.description}</div>
                )}

                <div className="bt-detail-grid">
                    <div className="bt-detail-cell">
                        <div className="bt-detail-label">Priority</div>
                        <div className="bt-detail-value">{f.priority || 'Normal'}</div>
                    </div>
                    <div className="bt-detail-cell">
                        <div className="bt-detail-label">Assigned Vendor</div>
                        <div className="bt-detail-value">
                            {pickedQuote?.vendor || (f.vendor && f.vendor !== 'TBD' ? f.vendor : '— Not yet assigned —')}
                        </div>
                    </div>
                    <div className="bt-detail-cell">
                        <div className="bt-detail-label">Date Requested</div>
                        <div className="bt-detail-value">{formatPretty(f.dateRequested)}</div>
                    </div>
                    <div className="bt-detail-cell">
                        <div className="bt-detail-label">Quotes Received</div>
                        <div className="bt-detail-value">{quotes.length}</div>
                    </div>
                    <div className="bt-detail-cell">
                        <div className="bt-detail-label">Final Cost</div>
                        <div className="bt-detail-value">
                            {f.actualCost && f.actualCost !== '$0' ? f.actualCost : '—'}
                        </div>
                    </div>
                </div>

                {!isClosed && (
                    <div className="bt-modal-section">
                        <div className="bt-modal-section-label">
                            <span role="img" aria-label="Quick">⚡</span> Quick Actions
                        </div>
                        <div className="bt-quick-actions">
                            <button
                                type="button"
                                className="bt-quick-btn"
                                onClick={() => setActiveAction(activeAction === 'quote' ? null : 'quote')}
                            >
                                <span role="img" aria-label="Quote">💰</span> Add Quote
                            </button>
                            <button
                                type="button"
                                className="bt-quick-btn"
                                onClick={() => setActiveAction(activeAction === 'note' ? null : 'note')}
                            >
                                <span role="img" aria-label="Note">📝</span> Add Note
                            </button>
                            <button
                                type="button"
                                className="bt-quick-btn"
                                onClick={() => setActiveAction(activeAction === 'photo' ? null : 'photo')}
                            >
                                <span role="img" aria-label="Photo">📷</span> Add Photo
                            </button>
                        </div>
                        {activeAction === 'quote' && (
                            <AddQuoteForm
                                onCancel={() => setActiveAction(null)}
                                onSave={handleAddQuote}
                            />
                        )}
                        {activeAction === 'note' && (
                            <AddNoteForm
                                onCancel={() => setActiveAction(null)}
                                onSave={handleAddNote}
                            />
                        )}
                        {activeAction === 'photo' && (
                            <AddPhotoForm
                                onCancel={() => setActiveAction(null)}
                                onSave={handleAddPhoto}
                            />
                        )}
                        {activeAction === 'complete' && (
                            <CompleteForm
                                defaultValue={f.quotedCost || ''}
                                onCancel={() => setActiveAction(null)}
                                onSave={handleMarkCompleted}
                            />
                        )}
                    </div>
                )}

                <div className="bt-modal-section" id="bt-quotes-section">
                    <div className="bt-modal-section-label">
                        <span role="img" aria-label="Quotes">💰</span> Quotes ({quotes.length})
                    </div>
                    {quotes.length === 0 ? (
                        <div className="bt-quotes-empty">No quotes yet.</div>
                    ) : (
                        <div className="bt-quotes-list">
                            {quotes.map((q, i) => (
                                <div key={i} className={`bt-quote-card${q.assigned ? ' picked' : ''}`}>
                                    <div className="bt-quote-head">
                                        <div className="bt-quote-vendor">{q.vendor || '—'}</div>
                                        <div className="bt-quote-amount">{q.amount || '—'}</div>
                                    </div>
                                    {q.notes && <div className="bt-quote-notes">{q.notes}</div>}
                                    <div className="bt-quote-foot">
                                        <span className="bt-quote-date">{q.date || ''}</span>
                                        {q.assigned ? (
                                            <span className="bt-quote-picked">
                                                <span role="img" aria-label="Selected">✓</span> Selected vendor
                                            </span>
                                        ) : !isClosed ? (
                                            <span className="bt-quote-actions">
                                                <button
                                                    type="button"
                                                    className="bt-quote-btn pick"
                                                    onClick={() => handleChooseVendor(i)}
                                                >
                                                    Choose this vendor
                                                </button>
                                                <button
                                                    type="button"
                                                    className="bt-quote-btn remove"
                                                    onClick={() => handleRemoveQuote(i)}
                                                >
                                                    Remove
                                                </button>
                                            </span>
                                        ) : null}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {photos.length > 0 && (
                    <div className="bt-modal-section">
                        <div className="bt-modal-section-label">
                            <span role="img" aria-label="Photos">📷</span> Photos ({photos.length})
                        </div>
                        <div className="bt-photos-list">
                            {photos.map((p, i) => (
                                <div key={i} className="bt-photo-card">
                                    <div className="bt-photo-thumb"><span role="img" aria-label="Photo">📷</span></div>
                                    <div className="bt-photo-info">
                                        <div className="bt-photo-name">{p.name}</div>
                                        <div className="bt-photo-date">{p.date}</div>
                                    </div>
                                    {!isClosed && (
                                        <button
                                            type="button"
                                            className="bt-photo-remove"
                                            onClick={() => handleRemovePhoto(i)}
                                            aria-label="Remove photo"
                                        >
                                            ✕
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="bt-modal-section">
                    <div className="bt-modal-section-label">
                        <span role="img" aria-label="Updates">📋</span> Updates &amp; History
                    </div>
                    {updates.length === 0 ? (
                        <div className="bt-quotes-empty">No updates yet.</div>
                    ) : (
                        <div className="bt-timeline">
                            {updates.slice().reverse().map((u, i) => (
                                <div key={i} className="bt-timeline-item">
                                    <div className="bt-timeline-icon" role="img" aria-label="Update">
                                        {u.icon || '•'}
                                    </div>
                                    <div className="bt-timeline-content">
                                        <div className="bt-timeline-type">{u.type || 'Update'}</div>
                                        <div
                                            className="bt-timeline-text"
                                            // eslint-disable-next-line react/no-danger
                                            dangerouslySetInnerHTML={{ __html: u.text || '' }}
                                        />
                                        <div className="bt-timeline-date">{u.date || ''}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="bt-modal-actions">
                    <button
                        className="bt-btn bt-btn-ghost"
                        onClick={onClose}
                        type="button"
                    >
                        Close
                    </button>
                    {status === 'Requested' && quotes.length === 0 && (
                        <button
                            className="bt-btn bt-btn-save"
                            onClick={() => setActiveAction('quote')}
                            type="button"
                        >
                            + Add First Quote
                        </button>
                    )}
                    {status === 'Requested' && quotes.length > 0 && !pickedQuote && (
                        <button
                            className="bt-btn bt-btn-save"
                            onClick={handleAssignScroll}
                            type="button"
                        >
                            → Assign Vendor
                        </button>
                    )}
                    {status === 'Assigned' && (
                        <button
                            className="bt-btn bt-btn-save"
                            onClick={handleStartWork}
                            type="button"
                        >
                            → Start Work
                        </button>
                    )}
                    {status === 'In Progress' && (
                        <button
                            className="bt-btn bt-btn-save"
                            onClick={() => setActiveAction('complete')}
                            type="button"
                        >
                            → Mark Completed
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// ----------------------------------------------------------------------
// Inline forms — replace window.prompt usage. Each renders into the
// "Quick Actions" section of the order detail popup.
// ----------------------------------------------------------------------

function AddQuoteForm({ onCancel, onSave }) {
    const [vendor, setVendor] = useState('');
    const [amount, setAmount] = useState('');
    const [notes, setNotes] = useState('');
    const submit = () => {
        if (!vendor.trim() || !amount.trim()) return;
        onSave(vendor, amount, notes);
    };
    return (
        <div className="bt-inline-form">
            <div className="bt-inline-form-title">Add a quote</div>
            <div className="bt-field">
                <label className="bt-field-label">Vendor *</label>
                <input
                    type="text"
                    className="bt-field-input"
                    value={vendor}
                    onChange={(e) => setVendor(e.target.value)}
                    placeholder="e.g. CoolTech HVAC"
                />
            </div>
            <div className="bt-field">
                <label className="bt-field-label">Amount *</label>
                <input
                    type="text"
                    className="bt-field-input"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="e.g. $850"
                />
            </div>
            <div className="bt-field">
                <label className="bt-field-label">Notes</label>
                <textarea
                    className="bt-field-input"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Optional details about this quote"
                    rows={2}
                />
            </div>
            <div className="bt-inline-form-actions">
                <button type="button" className="bt-btn bt-btn-ghost" onClick={onCancel}>
                    Cancel
                </button>
                <button type="button" className="bt-btn bt-btn-save" onClick={submit}>
                    Save Quote
                </button>
            </div>
        </div>
    );
}

function AddNoteForm({ onCancel, onSave }) {
    const [text, setText] = useState('');
    return (
        <div className="bt-inline-form">
            <div className="bt-inline-form-title">Add a note</div>
            <div className="bt-field">
                <label className="bt-field-label">Note *</label>
                <textarea
                    className="bt-field-input"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Add an update, follow-up, or comment..."
                    rows={3}
                />
            </div>
            <div className="bt-inline-form-actions">
                <button type="button" className="bt-btn bt-btn-ghost" onClick={onCancel}>
                    Cancel
                </button>
                <button
                    type="button"
                    className="bt-btn bt-btn-save"
                    onClick={() => onSave(text)}
                >
                    Save Note
                </button>
            </div>
        </div>
    );
}

function AddPhotoForm({ onCancel, onSave }) {
    const [desc, setDesc] = useState('');
    return (
        <div className="bt-inline-form">
            <div className="bt-inline-form-title">Add photo entry</div>
            <div className="bt-field-hint">
                Photo uploads are not yet supported — record a description for now.
            </div>
            <div className="bt-field">
                <label className="bt-field-label">Description *</label>
                <input
                    type="text"
                    className="bt-field-input"
                    value={desc}
                    onChange={(e) => setDesc(e.target.value)}
                    placeholder="e.g. Before repair — leaking pipe under sink"
                />
            </div>
            <div className="bt-inline-form-actions">
                <button type="button" className="bt-btn bt-btn-ghost" onClick={onCancel}>
                    Cancel
                </button>
                <button
                    type="button"
                    className="bt-btn bt-btn-save"
                    onClick={() => onSave(desc)}
                >
                    Save Entry
                </button>
            </div>
        </div>
    );
}

function CompleteForm({ defaultValue, onCancel, onSave }) {
    const [cost, setCost] = useState(defaultValue || '');
    return (
        <div className="bt-inline-form">
            <div className="bt-inline-form-title">Mark order completed</div>
            <div className="bt-field">
                <label className="bt-field-label">Final / Actual Cost *</label>
                <input
                    type="text"
                    className="bt-field-input"
                    value={cost}
                    onChange={(e) => setCost(e.target.value)}
                    placeholder="e.g. $720"
                />
            </div>
            <div className="bt-inline-form-actions">
                <button type="button" className="bt-btn bt-btn-ghost" onClick={onCancel}>
                    Cancel
                </button>
                <button
                    type="button"
                    className="bt-btn bt-btn-save"
                    onClick={() => onSave(cost)}
                >
                    Mark Completed
                </button>
            </div>
        </div>
    );
}

export default MaintenanceView;
